// TEMPORARY verification harness for the streamed-SOG dev path (Phase 1+2 —
// see /Users/uldis.liepins/.claude/plans/witty-watching-star.md). Not part
// of the feature; exercises SplatLoader.loadStreamed()/SplatPool/
// SplatOctree directly, bypassing Scene.js/SceneHandler.js/auth entirely so
// it can be driven headlessly without a logged-in session. Delete this file
// and its route in App.js once the streaming path has real coverage some
// other way (e.g. once it's wired into the normal auth'd scene flow).
//
// Also a STATIC-file + renderer harness for the WebGPU TSL port
// (WEBGPU-TSL-PORT-PLAN.md): ?static=<url>&renderer=webgpu|webgl loads a
// flat .sog/.ply/.splat through the normal SplatLoader.load() + Splat path
// with no auth, and publishes window.__harnessStats (fps, splats) for a
// headless driver. Under WebGPU it uses the same choice as the app: three's
// renderer (TSL port + GPU sort), or ?webgpuAddon=1 for three's addon.
import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { WebGPURenderer } from 'three/webgpu';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { SplatLoader, Splat, createWebGPUSplatMesh, useWebGPUAddon } from './Splat.ts';

async function adapterLimits() {
  const a = await navigator.gpu?.requestAdapter({ powerPreference: 'high-performance' });
  return a ? { maxStorageBufferBindingSize: a.limits.maxStorageBufferBindingSize, maxBufferSize: a.limits.maxBufferSize, maxTextureDimension2D: a.limits.maxTextureDimension2D } : undefined;
}

// Static-file mode — see the header comment.
async function runStatic(container, params, url, setStatus, isDisposed) {
  const wantGpu = params.get('renderer') === 'webgpu';
  const renderer = wantGpu
    ? new WebGPURenderer({ antialias: false, alpha: true, powerPreference: 'high-performance', trackTimestamp: true, requiredLimits: await adapterLimits() })
    : new THREE.WebGLRenderer({ antialias: false, alpha: true, powerPreference: 'high-performance' });
  if (wantGpu) {
    await renderer.init();
    // Same as the app: linear output so splats blend in sRGB space.
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  }
  const pr = parseFloat(params.get('pixelRatio'));
  renderer.setPixelRatio(Number.isFinite(pr) ? pr : Math.min(window.devicePixelRatio, 2));
  renderer.setSize(window.innerWidth, window.innerHeight);
  container.appendChild(renderer.domElement);

  const scene = new THREE.Scene();
  scene.background = wantGpu ? new THREE.Color('#20252b').convertLinearToSRGB() : new THREE.Color('#20252b');
  const camera = new THREE.PerspectiveCamera(35, window.innerWidth / window.innerHeight, 0.1, 10000);
  const controls = new OrbitControls(camera, renderer.domElement);
  const backend = wantGpu ? (renderer.backend?.isWebGPUBackend ? 'webgpu' : 'webgl2-fallback') : 'webgl';
  setStatus(`loading (${backend})`);

  const loader = new SplatLoader(renderer, 250000, params.has('sh') ? parseInt(params.get('sh'), 10) : undefined, 100);
  loader.load(url, async (shared) => {
    if (isDisposed()) return;
    // Frame from the decoded positions (SOG columns are available here,
    // before the push; mesh space is (x, -y, -z) — see pushSOGData).
    const cols = shared.sogDataTable?.cols;
    if (cols?.x) {
      const n = cols.x.length, stepN = Math.max(1, Math.floor(n / 20000));
      const c = new THREE.Vector3(); let k = 0;
      for (let i = 0; i < n; i += stepN) { c.x += cols.x[i]; c.y -= cols.y[i]; c.z -= cols.z[i]; k++; }
      c.divideScalar(k);
      const d = [];
      for (let i = 0; i < n; i += stepN) d.push(Math.hypot(cols.x[i] - c.x, -cols.y[i] - c.y, -cols.z[i] - c.z));
      d.sort((a, b) => a - b);
      const r = d[Math.floor(d.length * 0.8)] || 5;
      camera.position.set(c.x, c.y + r * 0.3, c.z + r * 2.2);
      controls.target.copy(c);
      controls.update();
    }
    // ?camPos=x,y,z&camTarget=x,y,z override (same as the streamed mode).
    const pv = (v) => { const a = (v || '').split(',').map(Number); return a.length === 3 && a.every(Number.isFinite) ? new THREE.Vector3(...a) : null; };
    const cp = pv(params.get('camPos')), ct = pv(params.get('camTarget'));
    if (cp) { camera.position.copy(cp); if (ct) controls.target.copy(ct); controls.update(); }
    window.__harnessCam = () => ({ pos: camera.position.toArray(), target: controls.target.toArray() });
    const mesh = renderer.isWebGPURenderer && useWebGPUAddon
      ? await createWebGPUSplatMesh(shared)
      : new Splat(shared, camera, params.has('covClamp') ? { covClamp: parseFloat(params.get('covClamp')) } : {});
    scene.add(mesh);
    window.__harnessShared = shared;
    const wait = setInterval(() => {
      if (shared.loaded) { clearInterval(wait); setStatus(`loaded (${backend}) ${shared.numVertices} splats`); }
    }, 100);
  }, undefined, (err) => setStatus('error: ' + (err?.message ?? err)));

  // Optional slow auto-orbit (?orbit=1) so a headless run measures moving-
  // camera frames (sorts + full redraws), not a static one.
  // Test hook: render now and return the canvas (same task) — used to verify
  // a 2D-canvas copy of the WebGL/WebGPU drawing buffer works (SceneHandler's
  // sharpening fade relies on it).
  window.__harnessRenderNow = () => { renderer.render(scene, camera); return renderer.domElement; };
  const orbit = params.get('orbit') === '1';
  let raf, frames = 0, last = performance.now(), gpuSum = 0, gpuN = 0, compSum = 0, compN = 0;
  // ?finish=1 (WebGL) / ?repeat=N: as in streamed mode — real GPU time per
  // frame via a one-pixel read-back, the frame drawn N times.
  const gl = params.get('finish') === '1' && !renderer.isWebGPURenderer ? renderer.getContext() : null;
  const repeatDraws = Math.max(1, parseInt(params.get('repeat'), 10) || 1);
  const gpuMs = [];
  const px = new Uint8Array(4);
  const animate = () => {
    raf = requestAnimationFrame(animate);
    if (orbit) {
      const off = camera.position.clone().sub(controls.target);
      off.applyAxisAngle(new THREE.Vector3(0, 1, 0), 0.01);
      camera.position.copy(controls.target).add(off);
    }
    controls.update();
    const tR = performance.now();
    for (let k = 1; k < repeatDraws; k++) renderer.render(scene, camera);
    renderer.render(scene, camera);
    if (gl) { gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, px); gpuMs.push(performance.now() - tR); }
    frames++;
    // GPU time of the render pass (WebGPU timestamp queries) — independent
    // of presentation, which is very slow in headless Chrome's WebGPU.
    if (renderer.isWebGPURenderer && renderer.backend?.isWebGPUBackend) {
      renderer.resolveTimestampsAsync('render').then((ms) => {
        if (typeof ms === 'number' && ms > 0) { gpuSum += ms; gpuN++; }
      }).catch(() => {});
      renderer.resolveTimestampsAsync('compute').then((ms) => {
        if (typeof ms === 'number' && ms > 0) { compSum += ms; compN++; }
      }).catch(() => {});
    }
    const now = performance.now();
    if (now - last >= 1000) {
      window.__harnessStats = { fps: +(frames * 1000 / (now - last)).toFixed(1), backend, gpuMsPerFrame: gpuN ? +(gpuSum / gpuN).toFixed(2) : null, sortGpuMs: compN ? +(compSum / compN).toFixed(2) : null,
        frameGpuMs: gpuMs.length ? +[...gpuMs].sort((x, y) => x - y)[gpuMs.length >> 1].toFixed(1) : null, canvas: `${renderer.domElement.width}x${renderer.domElement.height}` };
      frames = 0; last = now; gpuSum = 0; gpuN = 0; compSum = 0; compN = 0; gpuMs.length = 0;
    }
  };
  animate();
  return () => {
    cancelAnimationFrame(raf);
    container.removeChild(renderer.domElement);
  };
}

// Streamed-SOG mode (default). ?renderer=webgpu runs it on WebGPURenderer
// (streamed pools write via GPUQueue.writeTexture; GPU sort over the pool).
async function runStreamed(container, params, modelUrl, budget, setStatus, isDisposed) {
  const wantGpu = params.get('renderer') === 'webgpu';
  const renderer = wantGpu
    ? new WebGPURenderer({ antialias: false, alpha: true, powerPreference: 'high-performance', trackTimestamp: true, requiredLimits: await adapterLimits() })
    : new THREE.WebGLRenderer({ antialias: false, powerPreference: 'high-performance' }); // as the app (SceneHandler)
  if (wantGpu) {
    await renderer.init();
    renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  }
  const pr = parseFloat(params.get('pixelRatio'));
  if (Number.isFinite(pr)) renderer.setPixelRatio(pr);
  renderer.setSize(window.innerWidth, window.innerHeight);
  container.appendChild(renderer.domElement);
  const backend = wantGpu ? (renderer.backend?.isWebGPUBackend ? 'webgpu' : 'webgl2-fallback') : 'webgl';
  // Pool as the app sizes it (SceneHandler): 1.5× the budget on WebGL.
  const poolCapacity = renderer.isWebGPURenderer ? budget : Math.round(budget * 1.5);

  const scene = new THREE.Scene();
  scene.background = wantGpu ? new THREE.Color('#20252b').convertLinearToSRGB() : new THREE.Color('#20252b');
  const camera = new THREE.PerspectiveCamera(35, window.innerWidth / window.innerHeight, 0.1, 10000);
  // ?fl=<mm>: the app's lens (35 mm film over the screen's longer side —
  // SceneHandler applyLens; the app's default is 18).
  const fl = parseFloat(params.get('fl'));
  if (fl > 0) {
    camera.filmGauge = 35;
    camera.setFocalLength(fl);
  }
  const controls = new OrbitControls(camera, renderer.domElement);
  setStatus(`loading (${backend})`);

  let octree = null;
  const loader = new SplatLoader(renderer, 250000, undefined, 100);
  // ?lodDist=&lodMult= — production passes 710 / 2.7 (SceneHandler); 0 = octree default.
  const lodDist = parseFloat(params.get('lodDist')) || 0;
  const lodMult = parseFloat(params.get('lodMult')) || 0;
  // Allocator/solver switches, same names as SceneHandler's query params.
  const flag = (k) => params.get(k) === '1';
  // exactPack defaults on, as in the app.
  loader.loadStreamed(modelUrl, budget, { lodDist, lodMult, exactPack: params.get('exactPack') !== '0', defrag: flag('defrag'), knapsack: flag('knapsack'), poolCapacity }).then((shared) => {
    if (isDisposed()) return;
    const mesh = new Splat(shared, camera, params.has('covClamp') ? { covClamp: parseFloat(params.get('covClamp')) } : {});
    scene.add(mesh);
    octree = shared.octree;
    window.__harnessShared = shared; // for console/driver inspection
    window.__harnessCamObj = camera; // e.g. octree.viewBlur(__harnessCamObj, innerHeight)
    window.__harnessMesh = mesh; // e.g. __harnessMesh.material.shDegree = 0 (live)
    window.__harnessLoadedAt = performance.now();

    // Content-weighted centre/radius framing (see parseLodMeta). ?start=inside
    // stands at the centre; otherwise pull back to see the bulk.
    const center = (shared.contentCenter && shared.contentCenter.clone()) ||
      (shared.bound && !shared.bound.isEmpty() ? shared.bound.getCenter(new THREE.Vector3()) : new THREE.Vector3());
    const radius = shared.contentRadius || 50;
    const pv = (v) => { const a = (v || '').split(',').map(Number); return a.length === 3 && a.every(Number.isFinite) ? new THREE.Vector3(...a) : null; };
    const cp = pv(params.get('camPos')), ct = pv(params.get('camTarget'));
    if (cp) {
      camera.position.copy(cp);
      controls.target.copy(ct || center);
    } else if (params.get('start') === 'inside') {
      camera.position.copy(center);
      controls.target.set(center.x + Math.max(5, radius * 0.05), center.y, center.z);
    } else {
      camera.position.set(center.x, center.y + radius * 0.35, center.z + radius * 2.4);
      controls.target.copy(center);
    }
    controls.update();
    window.__streamCam = () =>
      `camPos=${camera.position.toArray().map((n) => +n.toFixed(1)).join(',')}` +
      `&camTarget=${controls.target.toArray().map((n) => +n.toFixed(1)).join(',')}`;

    setStatus(`loaded (${backend}) streamed`);
  }).catch((err) => {
    console.error('[harness] loadStreamed failed', err);
    setStatus('error: ' + (err?.message ?? err));
  });

  const orbit = params.get('orbit') === '1';
  // ?turn=<degrees per second>: turn in place (the target circles the camera)
  // — what a viewer does standing in one spot; frame-rate independent.
  const turnDegPerSec = parseFloat(params.get('turn')) || 0;
  let raf, frames = 0, last = performance.now(), gpuSum = 0, gpuN = 0, compSum = 0, compN = 0;
  let prevT = performance.now();
  const dts = []; // frame intervals over the current 1 s window
  // ?finish=1 (WebGL): time each frame's render + gl.finish() — how long the
  // GPU really takes, independent of the display's 60 Hz rounding.
  const finishTiming = params.get('finish') === '1' && !renderer.isWebGPURenderer;
  const gl = finishTiming ? renderer.getContext() : null;
  // ?finish=1 (WebGPU): each frame waits for queue.onSubmittedWorkDone()
  // before the next is requested — real GPU frame time, and fps that count
  // finished frames (WebKit otherwise queues frames far ahead of the GPU).
  const gpuDevice = params.get('finish') === '1' && renderer.isWebGPURenderer ? renderer.backend?.device : null;
  const gpuMs = [];
  const repeatDraws = Math.max(1, parseInt(params.get('repeat'), 10) || 1);

  const _px = new Uint8Array(4);
  const yAxis = new THREE.Vector3(0, 1, 0);
  const animate = () => {
    if (!gpuDevice) raf = requestAnimationFrame(animate);
    const tNow = performance.now();
    const dt = tNow - prevT;
    prevT = tNow;
    dts.push(dt);
    // window.__turn (deg/s) overrides ?turn= live — e.g. turn away, then stop.
    const turnNow = window.__turn ?? turnDegPerSec;
    if (turnNow && octree) {
      const off = controls.target.clone().sub(camera.position);
      off.applyAxisAngle(yAxis, THREE.MathUtils.degToRad(turnNow) * Math.min(dt, 100) / 1000);
      controls.target.copy(camera.position).add(off);
    }
    // window.__slide = <metres per second>: strafe sideways (camera and
    // target together, no rotation) — set from the driver once loaded, to
    // look for flicker while walking past something close.
    // window.__nudge = <metres>: one sideways step on the next frame only.
    if (window.__nudge && octree) {
      const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0).multiplyScalar(window.__nudge);
      camera.position.add(right);
      controls.target.add(right);
      window.__nudge = 0;
    }
    if (window.__slide && octree) {
      const right = new THREE.Vector3().setFromMatrixColumn(camera.matrixWorld, 0);
      // window.__slideStep (metres per FRAME) instead: same step every frame
      // whatever the frame rate, so flicker scores compare across settings.
      right.multiplyScalar(window.__slideStep || window.__slide * Math.min(dt, 100) / 1000);
      camera.position.add(right);
      controls.target.add(right);
    }
    // window.__walk = <metres per second>: walk forward along the level view
    // direction (camera and target together) — like the walk joystick.
    if (window.__walk && octree) {
      const fwd = controls.target.clone().sub(camera.position).setY(0).normalize();
      fwd.multiplyScalar(window.__walk * Math.min(dt, 100) / 1000);
      camera.position.add(fwd);
      controls.target.add(fwd);
    }
    if (orbit) {
      const off = camera.position.clone().sub(controls.target);
      off.applyAxisAngle(new THREE.Vector3(0, 1, 0), 0.01);
      camera.position.copy(controls.target).add(off);
    }
    controls.update();
    octree?.update(camera);
    const tR = performance.now();
    // ?repeat=N: draw the frame N times (timing: the extra draws' cost is
    // the real per-draw GPU time, without the read-back's fixed overhead).
    for (let k = 1; k < repeatDraws; k++) renderer.render(scene, camera);
    renderer.render(scene, camera);
    // window.__grab = { n, w, h }: read back a w×h patch at the canvas
    // centre on each of the next n frames into window.__grabbed (flicker
    // measurement — a pixel jumping back and forth between frames, which
    // smooth motion doesn't do).
    if (window.__grab && window.__grab.n > 0 && !renderer.isWebGPURenderer) {
      const g = window.__grab, c = renderer.getContext();
      const px = new Uint8Array(g.w * g.h * 4);
      c.readPixels((c.drawingBufferWidth - g.w) >> 1, (c.drawingBufferHeight - g.h) >> 1, g.w, g.h, c.RGBA, c.UNSIGNED_BYTE, px);
      (window.__grabbed = window.__grabbed || []).push(Array.from(px));
      g.n--;
    }
    // readPixels of one pixel: WebKit's finish() returns immediately, a
    // read-back really waits for the GPU to finish the frame.
    if (gl) { gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, _px); gpuMs.push(performance.now() - tR); }
    if (gpuDevice) {
      gpuDevice.queue.onSubmittedWorkDone().then(() => {
        gpuMs.push(performance.now() - tR);
        raf = requestAnimationFrame(animate);
      });
    }
    frames++;
    if (renderer.isWebGPURenderer && renderer.backend?.isWebGPUBackend) {
      renderer.resolveTimestampsAsync('render').then((ms) => { if (ms > 0) { gpuSum += ms; gpuN++; } }).catch(() => {});
      renderer.resolveTimestampsAsync('compute').then((ms) => { if (ms > 0) { compSum += ms; compN++; } }).catch(() => {});
    }
    const now = performance.now();
    if (now - last >= 1000) {
      const sorted = [...dts].sort((a, b) => a - b);
      const q = (p) => sorted.length ? sorted[Math.min(sorted.length - 1, Math.floor(p * sorted.length))] : 0;
      const median = q(0.5);
      const o = window.__splatOctreeStats || {};
      window.__harnessStats = {
        fps: +(frames * 1000 / (now - last)).toFixed(1), backend,
        // Frame pacing: percentiles of the frame interval and how many frames
        // took over 1.5× the median (and over 24 ms) — the stutter.
        p50: +median.toFixed(1), p95: +q(0.95).toFixed(1), max: +q(1).toFixed(1),
        slow: dts.filter((d) => d > 24 && d > median * 1.5).length,
        gpuMsPerFrame: gpuN ? +(gpuSum / gpuN).toFixed(2) : null, sortGpuMs: compN ? +(compSum / compN).toFixed(2) : null,
        // WebGPU: frames that ran the GPU sort in this second, and its GPU time
        // per drawn frame on average (sorting less often lowers this).
        sortsPerSec: compN, sortMsPerFrame: frames ? +(compSum / frames).toFixed(2) : null,
        resident: octree?.pool?.residentCount ?? null,
        sorted: o.sortedSplats ?? null, sortMs: o.sortMs ?? null,
        swaps: o.writesTotal ?? null, canvas: `${renderer.domElement.width}x${renderer.domElement.height}`,
        // Render + GPU completion per frame (?finish=1): median / max over the window.
        frameGpuMs: gpuMs.length ? +[...gpuMs].sort((a, b) => a - b)[Math.floor(gpuMs.length / 2)].toFixed(1) : null,
        frameGpuMaxMs: gpuMs.length ? +Math.max(...gpuMs).toFixed(1) : null,
        yaw: Math.round(THREE.MathUtils.radToDeg(Math.atan2(controls.target.x - camera.position.x, controls.target.z - camera.position.z))),
      };
      // ?hud=1: the numbers on screen (testing on a device without a driver).
      if (params.get('hud') === '1') {
        const h = window.__harnessStats, el = document.getElementById('harness-status');
        if (el) el.textContent = `${window.__harnessStatus || ''} · ${h.fps} fps · frame ${h.frameGpuMs ?? '-'} ms · draw ${h.gpuMsPerFrame ?? '-'} ms · sort ${h.sortGpuMs ?? '-'} ms · ${h.canvas} · ${((h.resident || 0) / 1e6).toFixed(2)}M loaded`;
      }
      gpuMs.length = 0;
      frames = 0; last = now; gpuSum = 0; gpuN = 0; compSum = 0; compN = 0; dts.length = 0;
    }
  };
  animate();

  const onResize = () => {
    camera.aspect = window.innerWidth / window.innerHeight;
    camera.updateProjectionMatrix();
    renderer.setSize(window.innerWidth, window.innerHeight);
  };
  window.addEventListener('resize', onResize);

  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener('resize', onResize);
    container.removeChild(renderer.domElement);
  };
}

export default function StreamTestHarness() {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const params = new URLSearchParams(window.location.search);
    const modelUrl = params.get('model') || '/test-fixtures/lod-demo/lod-meta.json';
    const budget = parseInt(params.get('budget'), 10) || 4_000_000;

    const staticUrl = params.get('static');
    let disposed = false;
    let statusEl = document.getElementById('harness-status');
    const setStatus = (t) => { if (statusEl) statusEl.textContent = t; window.__harnessStatus = t; };
    if (staticUrl) {
      runStatic(container, params, staticUrl, setStatus, () => disposed).then((cleanup) => {
        if (disposed) cleanup?.(); else window.__harnessCleanup = cleanup;
      });
      return () => { disposed = true; window.__harnessCleanup?.(); };
    }

    runStreamed(container, params, modelUrl, budget, setStatus, () => disposed).then((cleanup) => {
      if (disposed) cleanup?.(); else window.__harnessCleanup = cleanup;
    });
    return () => { disposed = true; window.__harnessCleanup?.(); };
  }, []);

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#111' }}>
      <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
      <div id="harness-status" style={{ position: 'fixed', top: 8, left: 8, color: '#0f0', fontFamily: 'monospace', fontSize: 12 }}>
        loading…
      </div>
    </div>
  );
}
